import React, { useCallback, useMemo } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { json } from "@codemirror/lang-json";
import { foldGutter } from "@codemirror/language";
import { EditorView, keymap, lineNumbers } from "@codemirror/view";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { toast } from "react-toastify";

export interface CmsCodeMirrorJsonEditorProps {
  label: string;
  required?: boolean;
  value: string;
  onChange: (next: string) => void;
  height?: number;
  readOnly?: boolean;
  error?: string | null;
  helperText?: string;
  onInsertSample?: () => void;
}

function formatJsonString(raw: string): string | null {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return null;
  }
}

const CmsCodeMirrorJsonEditor: React.FC<CmsCodeMirrorJsonEditorProps> = ({
  label,
  required,
  value,
  onChange,
  height = 320,
  readOnly,
  error,
  helperText,
  onInsertSample,
}) => {
  const extensions = useMemo(
    () => [
      history(),
      keymap.of([...defaultKeymap, ...historyKeymap]),
      lineNumbers(),
      foldGutter(),
      json(),
      EditorView.lineWrapping,
      EditorView.theme({
        "&": { fontSize: "13px", textAlign: "left" },
        ".cm-content": {
          paddingBlock: "10px",
          caretColor: "#0555c8",
          textAlign: "left",
        },
        ".cm-line": { textAlign: "left" },
        ".cm-scroller": {
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace",
          textAlign: "left",
        },
        ".cm-gutters": {
          backgroundColor: "#f8fafc",
          border: "none",
          borderRight: "1px solid #e2e8f0",
        },
        ".cm-activeLineGutter": { backgroundColor: "#e0f2fe" },
      }),
    ],
    []
  );

  const formatDocument = useCallback(() => {
    const next = formatJsonString(value);
    if (next === null) {
      toast.error("Cannot format: invalid JSON.");
      return;
    }
    onChange(next);
  }, [value, onChange]);

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: error ? "error.light" : "divider",
        overflow: "hidden",
        bgcolor: "background.paper",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
        "& .cm-editor": { outline: "none", textAlign: "left" },
        "& .cm-editor.cm-focused": { outline: "none" },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "grey.50",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Typography variant="subtitle2" fontWeight={700} component="span">
            {label}
            {required ? (
              <Typography component="span" color="error.main" fontWeight={700}>
                {" "}
                *
              </Typography>
            ) : null}
          </Typography>
          {error ? (
            <Chip size="small" color="error" variant="outlined" label="Syntax / validation" />
          ) : null}
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            startIcon={<AutoFixHighIcon fontSize="small" />}
            onClick={formatDocument}
            disabled={readOnly}
          >
            Format
          </Button>
          {onInsertSample && !readOnly ? (
            <Button size="small" variant="contained" disableElevation onClick={onInsertSample}>
              Insert sample
            </Button>
          ) : null}
        </Stack>
      </Stack>
      {helperText ? (
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1, display: "block" }}>
          {helperText}
        </Typography>
      ) : null}
      {error ? (
        <Typography variant="caption" color="error" sx={{ px: 2, pb: 0.5, display: "block" }}>
          {error}
        </Typography>
      ) : null}
      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          "& .cm-editor": { backgroundColor: "#fff" },
        }}
      >
        <CodeMirror
          value={value}
          height={`${height}px`}
          extensions={extensions}
          onChange={onChange}
          editable={!readOnly}
          basicSetup={false}
        />
      </Box>
    </Box>
  );
};

export default CmsCodeMirrorJsonEditor;
